import React, { useContext } from "react";
import { useImmer } from "use-immer";

import { useMutationCreateIssue, useAppContext } from "@examspark/common-app";
import {
  Button,
  FormContext,
  Label,
  Modal,
  SelectInput,
  TextArea,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { toast } from "@examspark/common-ui/dist/components/toast/Toast";
import { IssueType, Resource, ResourcePriorityEnum } from "@examspark/types";

import { WindowContext } from "./AppWrapper";

const defaultFormState = {
  type: IssueType.SpellingGrammar,
  title: "",
  description: "",
  priority: ResourcePriorityEnum.Medium,
};

type FeedbackProps = {
  resource: Pick<Resource, "id" | "sk" | "workspaceId">;
};
export const Feedback = (props: FeedbackProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const [state] = useAppContext();
  const { device } = useContext(WindowContext);

  const [form, updateForm] = useImmer(defaultFormState);

  const createIssueMutation = useMutationCreateIssue();

  // console.log({ state });

  const createIssue = async () => {
    setIsSaving(true);
    await createIssueMutation.mutateAsync({
      id: props.resource.id,
      sk: props.resource.sk,
      workspaceId: props.resource.workspaceId,
      creatorId: state.user?.id,
      name: form.title,
      description: form.description,
      type: form.type,
      priority: form.priority as ResourcePriorityEnum,
    });

    setIsSaving(false);
    updateForm((draft) => defaultFormState);
  };

  // this component makes no sense if we don't have a workspace ID,
  if (!props.resource.workspaceId) {
    return null;
  }

  // disabled at TCMPC's resuts (for now :frog: )
  return null;

  // eslint-disable-next-line no-unreachable
  return (
    <>
      <Button
        variant="outlined"
        title="Feedback"
        onClick={() => setIsOpen(true)}
      >
        <svg
          viewBox="0 0 512 512"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m493.886719 77.164062c-13.65625-51.203124-32.792969-77.164062-56.886719-77.164062-9.792969 0-18.765625 4.289062-26.886719 12.816406-44.890625 38.25-82.9375 63.664063-119.722656 79.96875-41.304687 18.304688-87.492187 26.824219-141.171875 26.050782-.789062-.015626-68.21875.164062-68.21875.164062-44.664062 0-81 36.347656-81 81.023438v81.027343c0 44.675781 36.335938 81.023438 81 81.023438 0 0 3.105469-.003907 8-.011719v100.9375c0 27.019531 21.980469 49 49 49h1.449219c21.242187 0 39.480469-13.121094 46.464843-33.445312l37.960938-110.929688c23.652344 4.308594 45.722656 11.019531 66.519531 20.234375 36.476563 16.167969 74.222657 41.328125 118.777344 79.179687.792969.675782 1.640625 1.242188 2.519531 1.722657 7.699219 7.472656 16.144532 11.238281 25.308594 11.238281 24.09375 0 43.230469-25.960938 56.886719-77.164062 11.679687-43.804688 18.113281-101.632813 18.113281-162.835938s-6.433594-119.03125-18.113281-162.835938zm-101.886719 162.835938c0-16.574219.484375-32.859375 1.429688-48.632812 18.160156 8.84375 30.570312 27.59375 30.570312 48.523437 0 20.941406-12.304688 39.707031-30.582031 48.554687-.9375-15.714843-1.417969-31.9375-1.417969-48.445312zm-362 41.050781v-81.027343c0-28.128907 22.878906-51.015626 51-51.015626h53v183.058594h-53c-28.121094 0-51-22.886718-51-51.015625zm127.539062 187.769531c-2.796874 8.128907-9.726562 13.179688-18.089843 13.179688h-1.449219c-10.476562 0-19-8.523438-19-19v-100.996094c16.488281-.042968 32.183594-.097656 32.578125-.167968 14.542969-.132813 28.511719.441406 41.992187 1.683593zm145.007813-108.386718c-41.289063-18.300782-86.734375-27.683594-138.546875-28.554688v-183.066406c51.742188-.878906 97.203125-10.277344 138.546875-28.601562 25.265625-11.195313 50.886719-26.234376 78.410156-46.125-.28125 1.019531-.566406 2.035156-.84375 3.074218-11.679687 43.804688-18.113281 101.636719-18.113281 162.839844s6.433594 119.035156 18.113281 162.835938c.351563 1.320312.710938 2.613281 1.070313 3.902343-27.632813-19.996093-53.316406-35.085937-78.636719-46.304687zm162.351563 34.671875c-12.152344 45.578125-25.097657 54.894531-27.898438 54.894531s-15.746094-9.316406-27.898438-54.894531c-5.820312-21.816407-10.253906-47.457031-13.152343-75.425781 34.113281-11.117188 58.050781-43.335938 58.050781-79.789063 0-20.09375-7.277344-39.566406-20.496094-54.824219-10.09375-11.65625-23.117187-20.242187-37.53125-24.953125 2.894532-27.890625 7.324219-53.457031 13.128906-75.222656 12.152344-45.574219 25.097657-54.890625 27.898438-54.890625s15.746094 9.316406 27.898438 54.894531c11.027343 41.351563 17.101562 96.4375 17.101562 155.105469s-6.074219 113.753906-17.101562 155.105469zm0 0" />
        </svg>
        Feedback
      </Button>
      <Modal
        title="Report Feedback"
        isOpen={isOpen}
        size={device === "phone" ? "mobile" : "medium"}
        onRequestClose={() => setIsOpen(false)}
        buttons={
          <Toolbar>
            <Button
              title="Cancel"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              title="Submit"
              onClick={() => {
                createIssue();
                toast.success("Feedback submitted!");
                setIsOpen(false);
              }}
            >
              Submit
            </Button>
          </Toolbar>
        }
      >
        <Modal.PaddedBody>
          <FormContext.Provider
            value={{
              disabled: isSaving,
            }}
          >
            <VSpace size="1.5rem">
              <Label label="Feedback Type">
                <SelectInput
                  value={form.type}
                  options={[
                    {
                      label: "Spelling or grammar errors exist.",
                      value: IssueType.SpellingGrammar,
                    },
                    {
                      label: "Subject matter is inaccurate.",
                      value: IssueType.SubjectMatter,
                    },
                    {
                      label: "Experiencing technical issues with this content.",
                      value: IssueType.Preview,
                    },
                    {
                      label:
                        "Answer key(s) or scoring is incorrect or incomplete.",
                      value: IssueType.Scoring,
                    },
                    {
                      label:
                        "Standards/alignments are incorrect or incomplete.",
                      value: IssueType.Alignment,
                    },
                    {
                      label: "Other",
                      value: IssueType.Other,
                    },
                  ]}
                  onChange={(e) => {
                    updateForm((draft) => {
                      draft.type = e?.value ?? IssueType.Other;
                    });
                  }}
                />
              </Label>
              <Label label="Title">
                <TextInput
                  value={form.title}
                  onChange={(e) => {
                    updateForm((draft) => {
                      draft.title = e.target.value;
                    });
                  }}
                />
              </Label>
              <Label label="Description">
                <TextArea
                  value={form.description}
                  onChange={(e) => {
                    updateForm((draft) => {
                      draft.description = e.target.value;
                    });
                  }}
                />
              </Label>
              <Label label="Priority">
                <SelectInput
                  value={form.priority}
                  options={[
                    {
                      label: "Critical",
                      value: ResourcePriorityEnum.Critical,
                    },
                    {
                      label: "High",
                      value: ResourcePriorityEnum.High,
                    },
                    {
                      label: "Medium",
                      value: ResourcePriorityEnum.Medium,
                    },
                    {
                      label: "Low",
                      value: ResourcePriorityEnum.Low,
                    },
                    {
                      label: "Informational",
                      value: ResourcePriorityEnum.Info,
                    },
                  ]}
                  onChange={(e) => {
                    updateForm((draft) => {
                      draft.priority = e?.value ?? ResourcePriorityEnum.Info;
                    });
                  }}
                />
              </Label>
            </VSpace>
          </FormContext.Provider>
        </Modal.PaddedBody>
      </Modal>
    </>
  );
};
